import { forwardRef, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { Edit16, Plus16 } from '@makyoapp/makyo-icon/icon'
import { Button, Checkbox, Chip, JustifyVariant, Modal, SelectDropdown, SimpleInput } from '@makyoapp/makyo-ui'

import { cn } from '@/lib/cn'

interface ArrayEditorProps {
  id: string
  value?: string[]
  onChange: (nextValue?: string[]) => void
  className?: string
}

const SEPARATOR_OPTIONS = [
  {
    value: ',',
    label: 'Comma',
  },
  {
    value: ' ',
    label: 'Space',
  },
  {
    value: ':',
    label: 'Colon',
  },
  {
    value: 'Custom',
    label: 'Custom',
  },
]

export const ArrayEditor = forwardRef<HTMLDivElement, ArrayEditorProps>(({ id, value, onChange, className }, ref) => {
  const [nextValues, setNextValues] = useState(value ?? [])
  const [tempValue, setTempValue] = useState('')
  const refInput = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()
  const [edit, setEdit] = useState<null | number>(null)
  const [multi, setMulti] = useBoolean(false)
  const [separator, setSeparator] = useState(',')
  const [customSeparator, setCustomSeparator] = useState('')

  return (
    <div
      ref={ref}
      className={cn(
        'relative max-w-[400px] bg-white dark:bg-slate-800 dark:text-zinc-50 border bdr flex flex-col gap-4',
        className
      )}
    >
      <Modal.Header onCloseClick={() => onChange()}>Enter Value</Modal.Header>
      <Modal.Body noPadding className='px-4 flex flex-col gap-4'>
        {nextValues.length > 0 && (
          <div className={clsx('flex flex-wrap gap-4 max-h-[600px] overflow-y-auto', 'pb-4 border-b bdr')}>
            {nextValues.map((c, i) => {
              return (
                <Chip
                  id={`${id}-item-${i}`}
                  defaultSize={false}
                  className='min-h-[1.5rem]'
                  key={i}
                  onRemove={() => {
                    const next = [...nextValues]
                    next.splice(i, 1)
                    setNextValues(next)
                    //props.node.setDataValue(id, next)
                  }}
                  after={
                    <button
                      id={`${id}-item-${i}-edit`}
                      className='shrink-0 hover:text-primary-500 flex-cc'
                      onClick={() => {
                        setEdit(i)
                        setTempValue(c)
                        setTimeout(() => refInput.current?.focus())
                      }}
                    >
                      <Edit16 />
                    </button>
                  }
                >
                  {c}
                </Chip>
              )
            })}
          </div>
        )}
        <div className='flex flex-col gap-2'>
          {edit === null && (
            <div className='h-10 flex-c gap-2 w-full text-m-sm'>
              <Checkbox
                justify={JustifyVariant.Start}
                label={<Trans>Multiple</Trans>}
                checked={multi}
                onClick={(isChecked) => setMulti(isChecked)}
              />
              {multi && (
                <div className='flex-c'>
                  <label>
                    <Trans>Separator</Trans>
                  </label>
                  <SelectDropdown
                    id={`${id}-separator-dd`}
                    toggleClassName={clsx(separator === 'Custom' ? 'w-[100px]' : 'w-[208px]')}
                    onChange={(opt, e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setSeparator(opt.value)
                    }}
                    menuClassName='text-m-sm'
                    menuFullWidth={false}
                    menuZIndex='z-100'
                    optionLabel={(opt) => `${opt.value} ${opt.label}`}
                    value={SEPARATOR_OPTIONS.find(({ value }) => value === separator)}
                    options={SEPARATOR_OPTIONS}
                    renderOption={(opt) => opt.label}
                  />
                  {separator === 'Custom' && (
                    <SimpleInput
                      className='w-[100px]'
                      inputClassName='min-w-0 mk-no-ag'
                      value={customSeparator}
                      onChange={(e) => {
                        const val = e.currentTarget.value
                        setCustomSeparator(val)
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
          <div className='flex-c'>
            <SimpleInput
              id={`${id}-item-input`}
              ref={refInput}
              value={tempValue}
              inputClassName='mk-no-ag'
              placeholder={edit ? t('Edit item') : t('Enter new item')}
              onChange={(e) => {
                const val = e.currentTarget.value
                setTempValue(val)
              }}
            />
            <Button
              id={`${id}-item-add`}
              disabled={!Boolean(tempValue)}
              noPadding
              squared
              icon={edit ? <Edit16 /> : <Plus16 />}
              onClick={() => {
                if (edit !== null) {
                  const next = [...nextValues]
                  next[edit] = tempValue
                  //props.node.setDataValue(id, next)
                  setNextValues(next)
                  setEdit(null)
                } else {
                  let nextTemp
                  if (multi) {
                    nextTemp = tempValue
                      .trim()
                      .split(separator === 'Custom' ? customSeparator : separator)
                      .map((v) => v.trim())
                  } else {
                    nextTemp = [tempValue]
                  }
                  const next = [...nextValues, ...nextTemp]
                  //props.node.setDataValue(id, next)
                  //onChange(next)
                  setNextValues(next)
                }
                setTempValue('')
              }}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className='justify-end'>
        <Button onClick={() => onChange(nextValues)}>
          <Trans>Save</Trans>
        </Button>
      </Modal.Footer>
    </div>
  )
})

ArrayEditor.displayName = 'ArrayEditor'
